import {RiskService} from "../service/RiskService";
import {BehaviorSubject, Observable, ReplaySubject, Scheduler} from "rxjs";
import {RiskSummary} from "../models/risk-model/RiskSummary";
import {RiskDashboardCollapseState} from "../models/view-model/RiskDashboardCollapseState";
import {RiskDashboardChangeModel, RowChanges} from "../models/view-model/RiskDashboardChangeModel";
import {NameFilter, NoOpNameFilter} from "../models/filtering/NameFilter";
import {ModelFilter, NoOpModelFilter} from "../models/filtering/ModelFilter";
import {defaultSortOptions, RiskSortOptions} from "../models/sorting/RiskSortOptions";
import {RiskDashboardViewModel} from "../models/view-model/RiskDashboardViewModel";
import {RiskRowEntityType} from "../models/RiskRowEntityType";
import {ObjectUtil} from "../../../js/util/ObjectUtil";
import {Pair, PairOf} from "../../../types/GenericTypes";
import {RiskLimits} from "../models/RiskSummaryRecord";
import {Optional} from "../../../js/util/Optional";
import {SystemEventService} from "../../../services/system-state/SystemEventService";
import {SystemEvent} from "../../../services/system-state/SystemEvent";
import {RiskDashboardNotifications} from "../models/RiskDashboardNotifications";
import {NotificationType} from "../components/notifications/NotificationType";
import {ClientEventService} from "../../../services/client-events/ClientEventService";
import {ClientEventType} from "../../../services/client-events/models/ClientEventType";

export class RiskDashboardViewStore {

    private readonly riskSummary$ = new ReplaySubject<RiskSummary>(1);
    private readonly nameFilter$ = new BehaviorSubject(NoOpNameFilter());
    private readonly modelFilter$ = new BehaviorSubject(NoOpModelFilter());
    private readonly sorting$ = new BehaviorSubject(defaultSortOptions());

    private readonly collapse$ = new BehaviorSubject(RiskDashboardCollapseState.init());
    private readonly changes$ = new BehaviorSubject(RiskDashboardChangeModel.init());

    private readonly model$ = new ReplaySubject<RiskDashboardViewModel>(1);

    private readonly notification$ = new ReplaySubject<RiskDashboardNotifications>(1);

    private readonly activeNotification$ = new BehaviorSubject<NotificationType | null>(null);

    private readonly totalStel$ = new ReplaySubject<number>(1);
    private readonly stelCash$ = new ReplaySubject<number>(1);

    private readonly persistPortfolio$ = new ReplaySubject<number>(1);
    private readonly persistOrg$ = new ReplaySubject<Pair<RiskRowEntityType, number>>(1);

    private readonly reload$ = new ReplaySubject<boolean>(1);

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    public constructor(private readonly service: RiskService,
                       private readonly systemEventService: SystemEventService,
                       private readonly clientEventService: ClientEventService) {
        this.init();
    }

    private init() {
        const underlyingModel$ = this.underlyingModel();
        this.viewModel(underlyingModel$)
            .takeUntil(this.destroyed$)
            .subscribe((model) => this.model$.next(model));
        this.reload$
            .debounceTime(100)
            .takeUntil(this.destroyed$)
            .subscribe(() => this.loadSummary());
        this.buildNotifications();
        this.initPortfolioSave();
        this.initOrgSave();
        this.initTotalStel();
        this.initlStelCash();
        this.watchUpdateEvent();
        this.watchIdleEnd();
        this.reload$.next(true);
    }

    private underlyingModel(): Observable<RiskSummary> {
        return Observable.combineLatest(this.riskSummary$, this.nameFilter$, this.modelFilter$, this.sorting$)
                         .debounceTime(100, Scheduler.async)
                         .takeUntil(this.destroyed$)
                         .map(([summary, nameFilter, modelFilter, sorting]) => {
                             const filtered = summary.filter(modelFilter, nameFilter);
                             return filtered.sort(sorting);
                         });
    }

    private viewModel(underlyingModel: Observable<RiskSummary>): Observable<RiskDashboardViewModel> {
        return Observable.combineLatest(underlyingModel, this.collapse$, this.changes$)
                         .debounceTime(100, Scheduler.async)
                         .takeUntil(this.destroyed$)
                         .map(([model, collapseState, changes]) => {
                             return RiskDashboardViewModel.build(model, collapseState, changes);
                         });
    }

    private async loadSummary() {
        try {
            const summary = await this.service.summary();
            const model = RiskSummary.build(summary);
            this.riskSummary$.next(model);
        } catch (e) {
            console.error(e);
        }
    }

    private buildNotifications() {
        this.riskSummary$
            .observeOn(Scheduler.async)
            .takeUntil(this.destroyed$)
            .map((model) => RiskDashboardNotifications.build(model))
            .subscribe((notifications) => this.notification$.next(notifications));
    }

    private initPortfolioSave() {
        this.persistPortfolio$
            .debounceTime(100)
            .withLatestFrom(this.model$)
            .takeUntil(this.destroyed$)
            .map(([portfolioId, model]) => this.portfolioLimits(portfolioId, model))
            .filter((o) => o.isPresent())
            .map((o) => o.get())
            .flatMap(([portfolioId, limits]) => this.updatePortfolio(portfolioId, limits))
            .subscribe(() => this.reload$.next(true));
    }

    private portfolioLimits(portfolioId: number, model: RiskDashboardViewModel): Optional<Pair<number, RiskLimits>> {
        return model.limits(RiskRowEntityType.CLIENT, portfolioId)
                    .map((limits) => PairOf(portfolioId, limits));
    }

    private updatePortfolio(portfolioId: number, limits: RiskLimits): Observable<void> {
        const request = this.service.updatePortfolioRiskLimits(portfolioId, limits);
        return Observable.fromPromise(request).catch(() => Observable.empty<void>());
    }

    private initOrgSave() {
        this.persistOrg$
            .debounceTime(100)
            .withLatestFrom(this.model$)
            .takeUntil(this.destroyed$)
            .map(([[type, id], model]) => this.orgLimits(type, id, model))
            .filter((o) => o.isPresent())
            .map((o) => o.get())
            .flatMap(([id, limits]) => this.updateOrg(id, limits))
            .subscribe(() => this.reload$.next(true));
    }

    private orgLimits(type: RiskRowEntityType,
                      id: number,
                      model: RiskDashboardViewModel): Optional<Pair<number, RiskLimits>> {
        return model.limits(type, id)
                    .map((limits) => PairOf(id, limits));
    }

    private updateOrg(id: number, limits: RiskLimits): Observable<void> {
        const request = this.service.updateOrgGroupRiskLimits(id, limits);
        return Observable.fromPromise(request).catch(() => Observable.empty<void>());
    }

    private initTotalStel() {
        this.riskSummary$
            .observeOn(Scheduler.async)
            .takeUntil(this.destroyed$)
            .map((model) => model.totalStel)
            .subscribe((stel) => this.totalStel$.next(stel));
    }

    private initlStelCash() {
        this.riskSummary$
            .observeOn(Scheduler.async)
            .takeUntil(this.destroyed$)
            .map((model) => model.stelCash)
            .subscribe((cash) => this.stelCash$.next(cash));
    }

    private watchUpdateEvent() {
        this.clientEventService
            .events()
            .filter((evt) => evt.type === ClientEventType.RISK_SUMMARIES_UPDATED)
            .takeUntil(this.destroyed$)
            .subscribe(() => this.reload$.next(true));
    }

    private watchIdleEnd() {
        this.systemEventService
            .eventsOfType(SystemEvent.IDLE_END)
            .takeUntil(this.destroyed$)
            .subscribe(() => this.reload$.next(true));
    }

    public toggleCollapse(type: RiskRowEntityType, id: number) {
        const state = this.collapse$.getValue();
        const currentCollapse = state.get(type, id);
        const updatedCollapse = !currentCollapse;
        const updatedState = state.set(type, id, updatedCollapse);
        this.collapse$.next(updatedState);
    }

    public collapseAll() {
        const currentState = this.collapse$.getValue();
        const updatedState = currentState.collapseAll();
        this.collapse$.next(updatedState);
    }

    public updateValues(type: RiskRowEntityType, id: number, changes: RowChanges) {
        const state = this.changes$.getValue();
        const currentChanges = state.get(type, id);
        const updatedChanges = ObjectUtil.merge({}, currentChanges, changes);
        const updatedState = state.set(type, id, updatedChanges);
        this.changes$.next(updatedState);
    }

    public updateNameFilter(filter: NameFilter) {
        this.nameFilter$.next(filter);
    }

    public updateModelFilter(filter: ModelFilter) {
        this.modelFilter$.next(filter);
    }

    public updateSorting(sortOptions: RiskSortOptions) {
        this.sorting$.next(sortOptions);
    }

    public persistPortfolioChanges(portfolioId: number) {
        this.persistPortfolio$.next(portfolioId);
    }

    public persistOrgChanges(type: RiskRowEntityType, id: number) {
        const request = PairOf(type, id);
        this.persistOrg$.next(request);
    }

    public toggleNotificationSelection(type: NotificationType) {
        const current = this.activeNotification$.getValue();
        if (current === type) {
            this.activeNotification$.next(null);
        } else {
            this.activeNotification$.next(type);
        }
    }

    public reset(type: RiskRowEntityType, id: number) {
        const state = this.changes$.getValue();
        const updated = state.reset(type, id);
        this.changes$.next(updated);
    }

    public get model(): Observable<RiskDashboardViewModel> {
        return this.model$.observeOn(Scheduler.async);
    }

    public get notifications(): Observable<RiskDashboardNotifications> {
        return this.notification$.observeOn(Scheduler.async);
    }

    public get activeNotification(): Observable<NotificationType | null> {
        return this.activeNotification$.observeOn(Scheduler.async);
    }

    public get totalStel(): Observable<number> {
        return this.totalStel$.observeOn(Scheduler.async);
    }

    public get stelCash(): Observable<number> {
        return this.stelCash$.observeOn(Scheduler.async);
    }

    public destroy() {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
